import { SecurityIssuedIn, TransactionType } from '@dealstack/shared/lib/generated';
import { HIDE_UNITS_TYPES } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';

interface ResolveTransactionTypeDerivedFieldsParams {
	transactionType?: TransactionType | null;
	issuerId?: string | null;
	securityIssuedIn?: SecurityIssuedIn | null;
	values: {
		sourceId?: string | null;
		destinationId?: string | null;
		cost?: number | null;
		units?: number | null;
	};
}

export const resolveTransactionTypeDerivedFields = ({
	transactionType,
	issuerId,
	securityIssuedIn,
	values,
}: ResolveTransactionTypeDerivedFieldsParams) => {
	const derivedValues = { ...values };

	/**
	 * Moves the counterparty to the side that can still hold it when the issuer takes over its
	 * field.
	 *
	 * An ISSUE's seller and a REDEMPTION's buyer are both the issuer, and both fields are hidden for
	 * those types, so the issuer has to be written in. Writing it over a REAL holder used to lose
	 * that holder outright: editing an issue (IssueCo -> Custodian) into a redemption kept IssueCo as
	 * the seller and set IssueCo as the buyer, producing a self-referential row that saved with a
	 * success toast and no warning. The holder was unrecoverable through the UI, because the field
	 * that had named them is not rendered for the new type.
	 *
	 * Carrying them across is what the edit actually means: "this was not a redemption from Bob, it
	 * was an issue to Bob". Only fills the opposite side when it is empty or is itself the issuer, so
	 * a genuine two-party row is never overwritten.
	 */
	const carryCounterparty = (from: 'sourceId' | 'destinationId') => {
		const to = from === 'sourceId' ? 'destinationId' : 'sourceId';
		const displaced = derivedValues[from];
		if (!displaced || displaced === issuerId) {
			return;
		}
		const opposite = derivedValues[to];
		if (!opposite || opposite === issuerId) {
			derivedValues[to] = displaced;
		}
	};

	switch (transactionType) {
		case TransactionType.ISSUE: {
			if (issuerId) {
				carryCounterparty('sourceId');
				derivedValues.sourceId = issuerId;
			}
			break;
		}
		case TransactionType.REDEMPTION: {
			if (issuerId) {
				carryCounterparty('destinationId');
				derivedValues.destinationId = issuerId;
			}
			break;
		}
		case TransactionType.CONVERSION_SHARE_CAPITAL: {
			if (issuerId) {
				derivedValues.sourceId = issuerId;
				derivedValues.destinationId = issuerId;
			}
			break;
		}
		case TransactionType.SHAREHOLDER_CONTRIBUTION: {
			if (issuerId) {
				derivedValues.sourceId = issuerId;
			}
			break;
		}
		case TransactionType.REDUCTION_IN_SHARE_CAPITAL:
		case TransactionType.DIVIDEND: {
			if (issuerId) {
				derivedValues.destinationId = issuerId;
			}
			break;
		}
		case TransactionType.CANCELLATION: {
			if (issuerId) {
				carryCounterparty('destinationId');
				derivedValues.destinationId = issuerId;
			}
			derivedValues.cost = 0;
			break;
		}
		default:
			break;
	}

	if (HIDE_UNITS_TYPES.includes(transactionType as TransactionType)) {
		derivedValues.units = 0;
	} else if (securityIssuedIn === SecurityIssuedIn.CURRENCY) {
		derivedValues.units = 1;
	}

	return derivedValues;
};
